import React from 'react';
import { styled } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { DateRangeType, DateType } from '@/js/libs/ui/src/Form/DatePicker/types';
import type { RangePreset } from '@/js/libs/ui/src/V2';
import { Label } from '@/js/libs/ui/src/V2';
import { getDataTestId } from '@/js/libs/utils/get-data-test-id';

import { DatePickerRange } from 'src/components/ui';

interface WrapperProps {
  $hasLabel: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  display: grid;
  grid-template-columns: ${({ $hasLabel }) => ($hasLabel ? 'auto 1fr' : '1fr')};
  grid-column-gap: 4px;
`;

export interface BaseProps {
  dateFrom: DateType;
  dateTo: DateType;
  maxDate?: Date;
  minDate?: Date;
  onSaveDate?: (value: DateRangeType | null) => void;
  disabled?: boolean;
  label?: string;
  dataTestId?: string;
  showLocal?: boolean;
}

interface GroupSetValueProps extends BaseProps {
  setDateFrom?: never;
  setDateTo?: never;
  setDate: React.Dispatch<React.SetStateAction<DateRangeType | null>>;
}

interface SeparatedSetValueProps extends BaseProps {
  setDateFrom: React.Dispatch<React.SetStateAction<DateType>>;
  setDateTo: React.Dispatch<React.SetStateAction<DateType>>;
  setDate?: never;
}

export type DateRangeFilterProps = (GroupSetValueProps | SeparatedSetValueProps) & {
  rangePresets?: RangePreset[];
};

export const DateRangeFilter: React.FC<DateRangeFilterProps> = React.memo(function DateRangeFilter({
  dateFrom,
  dateTo,
  setDate,
  setDateFrom,
  setDateTo,
  maxDate,
  minDate,
  onSaveDate,
  disabled,
  label,
  dataTestId,
  showLocal,
  rangePresets,
}) {
  const handleOnChange = useCallbackRef((value: DateType | DateRangeType | null) => {
    if (Array.isArray(value) && value.some((element) => element !== undefined && element !== null)) {
      setDateFrom?.(value[0]);
      setDateTo?.(value[1]);
      setDate?.(value);
      onSaveDate?.(value);
    } else {
      setDateFrom?.(null);
      setDateTo?.(null);
      setDate?.(null);
      onSaveDate?.(null);
    }
  });

  const dataTestIdValue = React.useMemo(
    () => (label && !dataTestId ? getDataTestId(label, 'date-range-filter') : dataTestId),
    [dataTestId, label],
  );

  return (
    <Wrapper $hasLabel={!!label} data-testid={dataTestIdValue}>
      {label && <Label noMargin>{label}:</Label>}
      <DatePickerRange
        startDate={dateFrom}
        endDate={dateTo}
        onChange={handleOnChange}
        dataTestId='date-input'
        maxDate={maxDate}
        minDate={minDate}
        isClearable
        disabled={disabled}
        showLocal={showLocal}
        rangePresets={rangePresets}
      />
    </Wrapper>
  );
});
